import type { Biorhythm, BiorhythmPhase } from './biorhythm';

/**
 * The lucky colors and the power crystal that go with a day's biorhythm,
 * ported from the prototype's biorhythm tab. Neither is in the blueprint: they
 * are prototype content the rebuild had dropped, carried here as one plan
 * feature (`biorhythm_lucky`) so a tenant decides who sees them.
 *
 * They are a reflective anchor for setting an intention, never a prediction
 * and never a remedy, which is what `COACHING_LUCKY_DISCLAIMER` says on every
 * screen that shows them.
 *
 * Both tables key off the three cycles the blueprint names. The spiritual
 * cycle is shown beside them but does not reshape them, exactly as it did not
 * in the prototype.
 */

/** Shown wherever the lucky colors or the crystal are displayed. */
export const COACHING_LUCKY_DISCLAIMER =
  'Lucky colors and stones are offered as a reflective anchor for setting an intention. They are not a prediction, a remedy or a treatment.';

export type LuckyColor = { name: string; hex: string };

/**
 * Which row of the color table a day lands on: the phases of the physical,
 * emotional and intellectual cycles in that order, with `any` where the phase
 * does not matter to the row.
 */
export const BIORHYTHM_LUCKY_KEYS = [
  'peak-peak-peak',
  'peak-peak-any',
  'peak-any-peak',
  'any-peak-peak',
  'peak-any-any',
  'any-peak-any',
  'any-any-peak',
  'critical-any-any',
  'any-critical-any',
  'any-any-critical',
  'low-low-low',
  'default',
] as const;
export type BiorhythmLuckyKey = (typeof BIORHYTHM_LUCKY_KEYS)[number];

export const BIORHYTHM_LUCKY_COLORS: Record<
  BiorhythmLuckyKey,
  { colors: LuckyColor[]; meaning: string }
> = {
  'peak-peak-peak': {
    colors: [
      { name: 'Gold', hex: '#B08D3F' },
      { name: 'White', hex: '#F5F0E8' },
      { name: 'Amber', hex: '#F59E0B' },
    ],
    meaning: 'All three cycles are high. Gold or white to match a day you can lean into.',
  },
  'peak-peak-any': {
    colors: [
      { name: 'Gold', hex: '#B08D3F' },
      { name: 'Violet', hex: '#7C3AED' },
      { name: 'Cream', hex: '#FEF9EF' },
    ],
    meaning: 'Creative and physical strength together. Gold anchors the day.',
  },
  'peak-any-peak': {
    colors: [
      { name: 'Green', hex: '#4ADE80' },
      { name: 'Gold', hex: '#B08D3F' },
      { name: 'Orange', hex: '#F97316' },
    ],
    meaning: 'Mind and body are aligned. Green suits a day of clear thinking.',
  },
  'any-peak-peak': {
    colors: [
      { name: 'Purple', hex: '#A78BFA' },
      { name: 'Green', hex: '#4ADE80' },
      { name: 'Silver', hex: '#C0C0C0' },
    ],
    meaning: 'Heart and mind in step. Purple for the conversations that matter.',
  },
  'peak-any-any': {
    colors: [
      { name: 'Red', hex: '#EF4444' },
      { name: 'Orange', hex: '#F97316' },
      { name: 'Gold', hex: '#B08D3F' },
    ],
    meaning: 'Physical energy leads today. Warm, bold colors match it.',
  },
  'any-peak-any': {
    colors: [
      { name: 'Purple', hex: '#A78BFA' },
      { name: 'Pink', hex: '#F472B6' },
      { name: 'Lavender', hex: '#C4B5FD' },
    ],
    meaning: 'Your emotional cycle is high. Purple and pink for connection.',
  },
  'any-any-peak': {
    colors: [
      { name: 'Green', hex: '#4ADE80' },
      { name: 'Teal', hex: '#14B8A6' },
      { name: 'White', hex: '#F5F0E8' },
    ],
    meaning: 'Mental clarity is your edge. Green supports clear thinking.',
  },
  'critical-any-any': {
    colors: [
      { name: 'Brown', hex: '#92400E' },
      { name: 'Beige', hex: '#D4C5A9' },
      { name: 'Olive', hex: '#84784A' },
    ],
    meaning: 'Grounding colors for a day your physical cycle is turning.',
  },
  'any-critical-any': {
    colors: [
      { name: 'Blue', hex: '#3B82F6' },
      { name: 'Indigo', hex: '#6366F1' },
      { name: 'White', hex: '#F5F0E8' },
    ],
    meaning: 'Calm colors while your emotional cycle crosses its midline.',
  },
  'any-any-critical': {
    colors: [
      { name: 'Gray', hex: '#6B7280' },
      { name: 'Silver', hex: '#C0C0C0' },
      { name: 'Pale Blue', hex: '#93C5FD' },
    ],
    meaning: 'Neutral tones to steady scattered thinking.',
  },
  'low-low-low': {
    colors: [
      { name: 'Navy', hex: '#1E3A5F' },
      { name: 'Charcoal', hex: '#374151' },
      { name: 'Deep Red', hex: '#991B1B' },
    ],
    meaning: 'A recharge day. Deep, restful colors suit it.',
  },
  default: {
    colors: [
      { name: 'Blue', hex: '#3B82F6' },
      { name: 'White', hex: '#F5F0E8' },
      { name: 'Silver', hex: '#C0C0C0' },
    ],
    meaning: 'A steady day. Cool colors for clear-headed decisions.',
  },
};

export type BiorhythmGem = { name: string; hex: string; benefit: string };

/**
 * The stone for the day, chosen by the PHYSICAL cycle's phase. The prototype
 * picked it this way and it is kept: a crystal is something the member carries
 * or wears, so the body-facing cycle is the one that names it.
 */
export const BIORHYTHM_GEMS: Record<BiorhythmPhase, BiorhythmGem> = {
  peak: {
    name: 'Citrine',
    hex: '#E0A82E',
    benefit: 'A reminder of abundance on a day you have the energy to act.',
  },
  rising: {
    name: "Tiger's Eye",
    hex: '#9A6B3F',
    benefit: 'A reminder to decide and move while your energy is building.',
  },
  critical: {
    name: 'Black Tourmaline',
    hex: '#2B2B2E',
    benefit: 'A grounding reminder to stay steady through a turning point.',
  },
  declining: {
    name: 'Moonstone',
    hex: '#DCE3E8',
    benefit: 'A reminder to ease down gently rather than push through.',
  },
  low: {
    name: 'Amethyst',
    hex: '#8B5FBF',
    benefit: 'A reminder that rest is part of the work, not a pause in it.',
  },
};

/**
 * Which color row a day lands on. The order matters: a triple peak wins over a
 * pair, a pair over a single, and a single peak over any turning point, so the
 * strongest thing true of the day is the thing the member is told about.
 */
export function biorhythmLuckyKey(
  physical: BiorhythmPhase,
  emotional: BiorhythmPhase,
  intellectual: BiorhythmPhase,
): BiorhythmLuckyKey {
  if (physical === 'peak' && emotional === 'peak' && intellectual === 'peak')
    return 'peak-peak-peak';
  if (physical === 'peak' && emotional === 'peak') return 'peak-peak-any';
  if (physical === 'peak' && intellectual === 'peak') return 'peak-any-peak';
  if (emotional === 'peak' && intellectual === 'peak') return 'any-peak-peak';
  if (physical === 'peak') return 'peak-any-any';
  if (emotional === 'peak') return 'any-peak-any';
  if (intellectual === 'peak') return 'any-any-peak';
  if (physical === 'critical') return 'critical-any-any';
  if (emotional === 'critical') return 'any-critical-any';
  if (intellectual === 'critical') return 'any-any-critical';
  if (physical === 'low' && emotional === 'low' && intellectual === 'low') return 'low-low-low';
  return 'default';
}

export type BiorhythmLucky = {
  key: BiorhythmLuckyKey;
  colors: LuckyColor[];
  meaning: string;
  gem: BiorhythmGem;
};

/** The day's lucky colors and stone, from a reading's three classic cycles. */
export function computeBiorhythmLucky(reading: Biorhythm): BiorhythmLucky {
  const key = biorhythmLuckyKey(
    reading.today.physical.phase,
    reading.today.emotional.phase,
    reading.today.intellectual.phase,
  );
  const row = BIORHYTHM_LUCKY_COLORS[key];
  return {
    key,
    colors: row.colors,
    meaning: row.meaning,
    gem: BIORHYTHM_GEMS[reading.today.physical.phase],
  };
}
